import { Input, ListItem, Text, Textarea, UnorderedList, VStack } from '@chakra-ui/react'
import { ReactNode, useEffect, useMemo, useState } from 'react'

import { CharCount } from '../../../components/CharCount'
import { DetailField } from '../../../components/DetailField'
import { FormField } from '../../../components/FormField'
import { FormModal } from '../../../components/FormModal'
import { ListInput } from '../../../components/ListInput'
import { LoadingBlock } from '../../../components/Loading'
import { FormSelect, SearchableSelect } from '../../../components/Select'
import { TabErrorLabel } from '../../../components/TabErrorLabel'
import { useAppToast } from '../../../helpers'
import { useTabFormErrors } from '../../../helpers/useTabFormErrors'
import { JobPositionService } from '../../../services/JobPosition/JobPositionService'
import {
  JobPosition,
  JobPositionDetail,
  JobPositionInput,
  JobPositionListField,
  OrganizationalLevel
} from '../../../services/JobPosition/types'
import { ISector, SectorService } from '../../../services/Sector/SectorService'
import {
  DESCRIPTION_MAX_LENGTH,
  JOB_POSITION_TAB_DEFS,
  LIST_FIELD_LABELS,
  LIST_FIELDS,
  LIST_ITEM_MAX_LENGTH,
  LIST_MAX_ITEMS,
  ORGANIZATIONAL_LEVEL_LABELS,
  ORGANIZATIONAL_LEVEL_OPTIONS
} from './constants'
import {
  IDocumentRow,
  JobPositionDocumentsEditor,
  toDocumentRows,
  validateDocuments
} from './JobPositionDocumentsEditor'

interface IJobPositionFormModalProps {
  isOpen: boolean
  onClose: () => void
  /** Called after a successful save, before closing. */
  onSaved: () => void
  /** The role to edit or view. Empty to create one. */
  jobPosition?: JobPosition
  /** Opens the role without editing (the "Ver" action). */
  isReadOnly?: boolean
}

interface IJobPositionForm {
  name: string
  sector_id: number
  organizational_level: OrganizationalLevel | ''
  duties: string[]
  education: string[]
  experience: string[]
  skills: string[]
  graduation_ids: number[]
  description: string
}

const baseForm = (): IJobPositionForm => ({
  name: '',
  sector_id: 0,
  organizational_level: '',
  duties: [],
  education: [],
  experience: [],
  skills: [],
  graduation_ids: [],
  description: ''
})

const toForm = (detail: JobPositionDetail): IJobPositionForm => ({
  name: detail.name,
  sector_id: detail.sector_id ?? 0,
  organizational_level: detail.organizational_level ?? '',
  duties: detail.duties ?? [],
  education: detail.education ?? [],
  experience: detail.experience ?? [],
  skills: detail.skills ?? [],
  graduation_ids: detail.graduations.map((graduation) => graduation.id),
  description: detail.description ?? ''
})

/** Client-side check mirroring the API rules, keyed like Laravel so the same rendering serves both. */
const validate = (form: IJobPositionForm, documents: IDocumentRow[]): Record<string, string[]> => {
  const errors: Record<string, string[]> = {}
  if (form.name.trim() === '') errors.name = ['O campo nome é obrigatório']
  if (!form.sector_id) errors.sector_id = ['Selecione o setor.']
  if (!form.organizational_level) errors.organizational_level = ['Selecione o nível organizacional.']
  LIST_FIELDS.forEach((field) => {
    if (form[field].length === 0) {
      errors[field] = [`Adicione pelo menos um item em ${LIST_FIELD_LABELS[field].toLowerCase()}.`]
    }
  })
  return { ...errors, ...validateDocuments(documents) }
}

/**
 * Create, edit or view a job role. Three tabs follow the client's requirements document:
 * general information, requirements and qualifications, and the documents the admission form asks for.
 */
export function JobPositionFormModal({
  isOpen,
  onClose,
  onSaved,
  jobPosition,
  isReadOnly = false
}: IJobPositionFormModalProps) {
  const service = new JobPositionService()
  const toast = useAppToast()
  const [form, setForm] = useState<IJobPositionForm>(baseForm)
  const [documents, setDocuments] = useState<IDocumentRow[]>([])
  const {
    errors: fieldErrors,
    setErrors: setFieldErrors,
    tabIndex,
    setTabIndex,
    errorCounts,
    clearErrors,
    listErrors,
    showErrors
  } = useTabFormErrors(JOB_POSITION_TAB_DEFS)
  const [saving, setSaving] = useState(false)
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [sectors, setSectors] = useState<ISector[]>([])
  const [loadingSectors, setLoadingSectors] = useState(false)
  // Every role of the registry, for the graduations: the page only holds the current page of roles.
  const [allJobPositions, setAllJobPositions] = useState<JobPosition[]>([])
  const [loadingRoles, setLoadingRoles] = useState(false)

  useEffect(() => {
    if (!isOpen) return

    setSaving(false)
    setFieldErrors({})
    setTabIndex(0)
    setForm(baseForm())
    setDocuments([])

    if (!isReadOnly && sectors.length === 0) {
      setLoadingSectors(true)
      new SectorService()
        .listForSelect()
        .then(setSectors)
        .catch((error) => toast.error('Falha ao carregar setores', error))
        .finally(() => setLoadingSectors(false))
    }

    setLoadingRoles(true)
    service
      .listAll()
      .then(setAllJobPositions)
      .catch((error) => toast.error('Erro ao carregar os cargos', error))
      .finally(() => setLoadingRoles(false))

    if (jobPosition) {
      setLoadingDetail(true)
      service
        .show(jobPosition.id)
        .then((detail) => {
          setForm(toForm(detail))
          setDocuments(toDocumentRows(detail.documents))
        })
        .catch((error) => {
          toast.error('Erro ao carregar o cargo', error)
          onClose()
        })
        .finally(() => setLoadingDetail(false))
    }
  }, [isOpen, jobPosition?.id])

  const graduationOptions = useMemo(
    () =>
      allJobPositions
        .filter((role) => role.id !== jobPosition?.id)
        // An inactive role stays listed only where it is already a graduation, flagged as such.
        .filter((role) => role.status === 'active' || form.graduation_ids.includes(role.id))
        .map((role) => ({
          value: role.id,
          label: role.status === 'active' ? role.name : `${role.name} (desativado)`
        })),
    [allJobPositions, jobPosition?.id, form.graduation_ids]
  )

  const updateForm = (patch: Partial<IJobPositionForm>) => {
    setForm((prev) => ({ ...prev, ...patch }))
    clearErrors(Object.keys(patch))
  }

  const updateDocuments = (rows: IDocumentRow[]) => {
    setDocuments(rows)
    clearErrors(['documents'])
  }

  const handleSave = () => {
    const errors = validate(form, documents)
    if (Object.keys(errors).length > 0) {
      showErrors(errors)
      return
    }

    const payload: JobPositionInput = {
      ...form,
      name: form.name.trim(),
      sector_id: form.sector_id || null,
      documents: documents.map(({ id, name, required }) => ({ id, name: name.trim(), required }))
    }

    setSaving(true)
    setFieldErrors({})
    const request = jobPosition ? service.update(jobPosition.id, payload) : service.create(payload)
    request
      .then(() => {
        toast.success(jobPosition ? 'Cargo atualizado' : 'Cargo cadastrado')
        onSaved()
        onClose()
      })
      .catch((error) => {
        const { status, data } = error?.response ?? {}
        // The API's handler answers every ValidationException with 400.
        if ((status === 400 || status === 422) && data?.errors) {
          showErrors(data.errors)
          return
        }
        toast.error('Erro ao salvar o cargo', error)
      })
      .finally(() => setSaving(false))
  }

  const tabLabel = (index: number) => (
    <TabErrorLabel label={JOB_POSITION_TAB_DEFS[index].label} count={errorCounts[index]} />
  )

  const disabled = saving || loadingDetail

  const listField = (field: JobPositionListField) =>
    isReadOnly ? (
      <DetailField key={field} label={LIST_FIELD_LABELS[field]}>
        <ListInput value={form[field]} onChange={() => undefined} isReadOnly />
      </DetailField>
    ) : (
      <FormField key={field} label={LIST_FIELD_LABELS[field]} error={listErrors(field)} isRequired>
        <ListInput
          value={form[field]}
          onChange={(items) => updateForm({ [field]: items })}
          maxItems={LIST_MAX_ITEMS}
          maxLength={LIST_ITEM_MAX_LENGTH}
          isDisabled={disabled}
        />
      </FormField>
    )

  const panel = (content: ReactNode) =>
    loadingDetail || (isReadOnly && loadingRoles) ? (
      <LoadingBlock entity="cargo" />
    ) : (
      <VStack align="stretch" spacing={5}>
        {content}
      </VStack>
    )

  const sectorName = sectors.find((sector) => sector.id === form.sector_id)?.name ?? jobPosition?.sector?.name
  const graduationNames = graduationOptions
    .filter((option) => form.graduation_ids.includes(option.value))
    .map((option) => option.label)

  const generalTab = isReadOnly ? (
    <>
      <DetailField label="Nome do Cargo" value={form.name} />
      <DetailField label="Setor" value={sectorName} />
      <DetailField
        label="Nível Organizacional"
        value={form.organizational_level ? ORGANIZATIONAL_LEVEL_LABELS[form.organizational_level] : null}
      />
      {listField('duties')}
      <DetailField label="Observações" value={form.description} />
    </>
  ) : (
    <>
      <FormField label="Nome do Cargo" error={fieldErrors.name} isRequired>
        <Input
          value={form.name}
          onChange={(e) => updateForm({ name: e.target.value })}
          maxLength={255}
          isDisabled={disabled}
        />
      </FormField>
      <FormField label="Setor" error={fieldErrors.sector_id} isRequired>
        <FormSelect
          value={form.sector_id}
          onChange={(value) => updateForm({ sector_id: Number(value) })}
          options={sectors.map((sector) => ({ value: sector.id, label: sector.name }))}
          emptyValue={0}
          isLoading={loadingSectors}
          isDisabled={disabled}
        />
      </FormField>
      <FormField label="Nível Organizacional" error={fieldErrors.organizational_level} isRequired>
        <FormSelect
          value={form.organizational_level}
          onChange={(value) => updateForm({ organizational_level: value as OrganizationalLevel | '' })}
          options={ORGANIZATIONAL_LEVEL_OPTIONS}
          isDisabled={disabled}
        />
      </FormField>
      {listField('duties')}
      <FormField
        label="Observações"
        error={fieldErrors.description}
        helperText="Opcional. Anotações internas do RH sobre o cargo."
      >
        <Textarea
          value={form.description}
          onChange={(e) => updateForm({ description: e.target.value })}
          maxLength={DESCRIPTION_MAX_LENGTH}
          rows={5}
          isDisabled={disabled}
        />
        <CharCount value={form.description} max={DESCRIPTION_MAX_LENGTH} />
      </FormField>
    </>
  )

  const requirementsTab = (
    <>
      {listField('education')}
      {listField('experience')}
      {listField('skills')}
      {isReadOnly ? (
        <DetailField label="Graduações do Cargo">
          {graduationNames.length === 0 ? (
            <Text variant="caption">Nenhuma graduação</Text>
          ) : (
            <UnorderedList spacing={1} pl={1}>
              {graduationNames.map((name) => (
                <ListItem key={name}>{name}</ListItem>
              ))}
            </UnorderedList>
          )}
        </DetailField>
      ) : (
        <FormField
          label="Graduações do Cargo"
          error={listErrors('graduation_ids')}
          helperText="Opcional. Outros cargos deste cadastro ligados a este."
        >
          <SearchableSelect<number>
            isMulti
            options={graduationOptions}
            value={form.graduation_ids}
            onChange={(ids) => updateForm({ graduation_ids: ids })}
            placeholder="Selecione um ou mais cargos"
            entity="cargo"
            isLoading={loadingRoles}
            isDisabled={disabled}
          />
        </FormField>
      )}
    </>
  )

  const title = isReadOnly ? (jobPosition?.name ?? 'Cargo') : jobPosition ? 'Editar cargo' : 'Adicionar cargo'

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      onSubmit={isReadOnly ? undefined : handleSave}
      size="editor"
      isLoading={saving}
      isConfirmDisabled={disabled}
      tabIndex={tabIndex}
      onTabChange={setTabIndex}
      // A required field on a hidden tab would make the browser block the submit silently.
      noValidate
      tabs={[
        { label: tabLabel(0), content: panel(generalTab) },
        { label: tabLabel(1), content: panel(requirementsTab) },
        {
          label: tabLabel(2),
          content: panel(
            <JobPositionDocumentsEditor
              rows={documents}
              onChange={updateDocuments}
              fieldErrors={fieldErrors}
              isDisabled={disabled}
              isReadOnly={isReadOnly}
            />
          )
        }
      ]}
    />
  )
}
